import { jsx } from '@amazon/vinyl-tsx'
import { combineData, type ObservableValue } from '@amazon/vinyl-observable'
import { Icon } from '@/components/Icon'
import { logError, type Maybe } from '@amazon/vinyl-util'
import { ctx } from '@/util/context'
import { toast } from '@/components/toast'
import { type ImageModel, ImageView } from '@/components/ImageView'
import { Modal, type ModalProps } from '@/components/Modal'
import { getString } from '@/share/i18n/bundle.generated'
import { getFacebookShareUrl, getMailShareUrl, getTwitterShareUrl } from '@/share/shareUrls'

export interface ShareModalProps extends ModalProps {
    readonly data: ObservableValue<Maybe<ShareModalModel>>
}

export interface ShareModalModel {
    readonly link: string | null
    readonly title: string | null
    readonly label: string | null
    /**
     * Localised entity label substituted into the share-modal title
     * ("Share artist", "Share playlist", …). Drives the title text;
     * `label` above is the small caps label shown on the card.
     */
    readonly entityType: string | null
    /**
     * Optional secondary line — e.g. an album's primary artist or a
     * track's artist. Empty for entities like artists / playlists
     * where there's no obvious secondary; omitted from the external
     * share text in that case (see `_no_secondary` key).
     */
    readonly secondary: string | null
    readonly description: string | null
    readonly image: ImageModel
}

export function ShareModal({ data, ...rest }: ShareModalProps) {
    const { target, errorHandler } = ctx('ShareModal')

    const clipboardSupported =
        'clipboard' in navigator && typeof navigator.clipboard.writeText === 'function'
    const copyToClipboard = () => {
        if (!data.value?.link) return
        navigator.clipboard
            .writeText(data.value.link)
            .then(() => {
                toast(getString('dmusic_share_modal_copied_to_clipboard').value)
            })
            .catch((error) => {
                logError(target, 'Failed to copy link:', error)
                errorHandler(error)
            })
    }

    /**
     * The share text we hand off to Twitter / mailto. Switches between
     * the with-secondary and no-secondary keys so we don't end up with
     * dangling "by undefined" copy when the entity has no secondary
     * (e.g. an artist).
     */
    const shareText = combineData({
        primary: data.map((d) => d?.title ?? ''),
        secondary: data.map((d) => d?.secondary ?? ''),
        link: data.map((d) => d?.link ?? ''),
    })

    function getShareTextValue(): string {
        const v = shareText.value
        const key = v.secondary
            ? 'dmusic_share_modal_external_share_text'
            : 'dmusic_share_modal_external_share_text_no_secondary'
        return getString(key, v).value
    }

    function shareOnFacebook() {
        const link = data.value?.link
        if (!link) return
        window.open(getFacebookShareUrl(link), '_blank', 'noopener,noreferrer')
    }

    function shareOnTwitter() {
        if (!data.value?.link) return
        window.open(getTwitterShareUrl(getShareTextValue()), '_blank', 'noopener,noreferrer')
    }

    function shareByMail() {
        if (!data.value?.link) return
        window.location.href = getMailShareUrl(getShareTextValue())
    }

    const titleObs = combineData({
        entityType: data.map((d) => d?.entityType ?? ''),
    }).map((params) =>
        params.entityType ? getString('dmusic_share_modal_share_title', params).value : ''
    )

    return (
        <Modal className="shareModal" {...rest}>
            <h2 className="title">{titleObs}</h2>
            <div className="playlistCard">
                <ImageView
                    image={data.pick('image')}
                    title={data.pick('title')}
                    className="playlistImage"
                />
                <div className="playlistInfo">
                    <small className="label">{data.pick('label')}</small>
                    <h3 className="playlistTitle">{data.pick('title')}</h3>
                    <div className="tertiaryText">{data.pick('description')}</div>
                </div>
            </div>
            <div className="linkSection">
                <label className="shareLabel">{getString('dmusic_share_modal_share_link')}</label>
                <input
                    className="shareInput"
                    type="text"
                    value={data.pick('link')}
                    readOnly={true}
                    onclick={(e) => (e.currentTarget as HTMLInputElement).select()}
                />
            </div>
            <div className="shareContainer">
                <button
                    className="iconButton"
                    aria-label={getString('dmusic_share_modal_share_facebook').value}
                    onclick={shareOnFacebook}
                >
                    <Icon icon="facebookcolor" />
                </button>
                <button
                    className="iconButton"
                    aria-label={getString('dmusic_share_modal_share_twitter').value}
                    onclick={shareOnTwitter}
                >
                    <Icon icon="twitter" />
                </button>
                <button
                    className="iconButton"
                    aria-label={getString('dmusic_share_modal_share_email').value}
                    onclick={shareByMail}
                >
                    <Icon icon="shareEmail" />
                </button>
                <div className="flexSpacer" />
                <button
                    className="button copyLink"
                    onclick={copyToClipboard}
                    visible={clipboardSupported}
                >
                    <Icon icon="shareLink" />
                    {getString('dmusic_share_modal_copy_link')}
                </button>
            </div>
        </Modal>
    )
}
